import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';

// actions / utils
import { loadConfig } from '../../../actions/siteConfig';
import { updateEnrollment } from '../../../actions/enrollment';
import { sendEsign } from '../../../actions/esign';
import { cap } from '../../../common/utils/prettify';
import { isValid } from '../../../utils/form';

// component
import { TextInput } from './components';
import { LoaderButton } from '../../../common/components';

function Preview({ greeting }) {
  const [ smsBlocks, setSMSBlocks ] = useState({});

  useEffect(() => {
    loadConfig('pinpoint_esign_sms_text_json', true)
      .then((data) => setSMSBlocks((data || {})));
  }, []);

  const linkClass = 'text-decoration-underline text-primary';

  return (
    <div
      style={{ wordBreak: 'break-word', borderLeft: 'solid 3px var(--bs-gray-300)' }}
      className="mb-5 ms-2 mt-2 ps-4 pt-2"
    >
      <p>
        <span className="bg-bgyellow">{greeting}</span>&nbsp;
        {smsBlocks.block_1}&nbsp;
        <span className={linkClass}>{'<link>'}</span>&nbsp;
        {smsBlocks.block_2}
        {' <activation code>. '}
        {smsBlocks.block_3}
      </p>
    </div>
  );
}

function SendShopperSMS() {
  const dispatch = useDispatch();
  const { values } = useSelector(store => store);
  const {
    first_name: shopperFirstName,
    home_phone: shopperHomePhone,
    mobile_phone: shopperMobilePhone,
    esignature_sms_timestamps: esigSMSTimestamps = [],
    quote: { quoteID },
  } = values;
  const lastSentTimestamp = esigSMSTimestamps.at(-1);
  const {
    firstName: agentFirstName,
    lastName: agentLastName,
  } = useSelector(store => store.agent);
  const { home_phone: phoneValidationRegex } = useSelector(store => store.validation);
  const { username: shopperUsername } = useSelector(store => store.shopper);
  const { submittedFlag } = useSelector(store => store.enrollment);

  function setInitialPhoneState(resetState = false) {
    if ((submittedFlag || lastSentTimestamp) && !resetState) return '';
    if (shopperMobilePhone) return shopperMobilePhone;
    if (shopperHomePhone) return shopperHomePhone;
    return '';
  }

  const [ loading, setLoading ] = useState(false);
  const [ phone, setPhone ] = useState(setInitialPhoneState());
  const [ greeting, setGreeting ] = useState(`Hi ${cap(shopperFirstName)}, this is ${cap(agentFirstName)} ${cap(agentLastName)}.`);
  const [ allowResend, setAllowResend ] = useState(!lastSentTimestamp);
  const [ sendError, setSendError ] = useState();

  function handleSubmit() {
    setLoading(true);
    setSendError('');
    const data = {
      phone,
      greeting,
      shopper_username: shopperUsername,
      quote_id: quoteID,
    };
    const valid = isValid({ value: phone, validationRegex: phoneValidationRegex });
    if (valid) {
      sendEsign('sms', data).then(() => {
        const updatedArray = [ ...esigSMSTimestamps, moment().toISOString() ];
        const updatedValues = { ...values, esignature_sms_timestamps: updatedArray };
        updateEnrollment({ values: updatedValues }).then(() => {
          dispatch({ type: 'SET_FIELD_VALUE', name: 'esignature_sms_timestamps', value: updatedArray });
        }).finally(() => {
          setLoading(false);
          setAllowResend(false);
        });
      }).catch(() => {
        setSendError('Error sending SMS');
      }).finally(() => {
        setLoading(false);
        setPhone('');
      });
    } else {
      setSendError('Invalid phone number');
      setLoading(false);
    }
  }

  return (
    <>
      <div className="row">
        <TextInput
          required
          label="Phone number"
          name="send-shopper-sms-phone"
          value={phone}
          handleChange={(e) => setPhone(e.target.value)}
        />
        <TextInput
          label="Greeting"
          name="send-shopper-sms-greeting"
          className="bg-bgyellow"
          value={greeting}
          handleChange={(e) => setGreeting(e.target.value)}
        />
      </div>

      <div className="row mt-4">
        <div className="col-md-4">
          <LoaderButton
            onClick={() => handleSubmit()}
            className="btn btn-primary fw-bold"
            text="Send SMS"
            loadingText="Sending"
            loading={loading}
            disabled={(lastSentTimestamp && !allowResend) || submittedFlag}
          />
          <br />
          {sendError && (
            <i className="text-danger">
              {sendError}
            </i>
          )}
        </div>

        <div className="d-flex col align-items-center justify-content-end">
          {lastSentTimestamp && (
            <>
              {!allowResend && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary me-4"
                  onClick={() => {
                    setAllowResend(true);
                    setPhone(setInitialPhoneState(true));
                  }}
                  disabled={submittedFlag}
                >
                  Enable Resend?
                </button>
              )}
              <i className="text-info fw-bold">
                Last sent {moment(lastSentTimestamp).format('MM/DD/YYYY h:mma')}
              </i>
            </>
          )}
        </div>
      </div>

      <div className="row mt-3">
        <Preview greeting={greeting} />
      </div>
    </>
  );
}

export default SendShopperSMS;
